import { entity, enumFromDictionary, enumeration, subentity } from '@gh/core-data';
import { ContactMergeDto } from '../model/contact-merge.dto';
import { ContactDisplay, ContactRoleType } from '../model/contact-details-common.dto';
import {
  ContactCustomerType,
  ContactDetailsCustomerAdditionalDetailsDto,
  ContactDetailsCustomerBillingDto,
  ContactDetailsCustomerCommonDto,
  ContactDetailsCustomerDto,
  ContactDetailsCustomerReferredByDto,
} from '../model/contact-details-customer.dto';
import {
  ContactDetailsEmployeeCommonDto,
  ContactDetailsEmployeeDto,
  ContactDetailsEmployeePersonalDto,
  ContactDetailsEmployeeSalaryDto,
} from '../model/contact-details-employee.dto';
import {
  BusinessNumberType,
  ContactDetailsCreditorBusinessInfoDto,
  ContactDetailsCreditorCommonDto,
  ContactDetailsCreditorDeliveryInfoDto,
  ContactDetailsCreditorDto,
  CreditorType,
  EmailMatch,
  SupplierCategoryType,
  SupplierServiceType,
  WithholdingTaxType,
} from '../model/contact-details-creditor.dto';
import {
  ContactDetailsDebtorBillingDto,
  ContactDetailsDebtorCommonDto,
  ContactDetailsDebtorDto,
} from '../model/contact-details-debtor.dto';
import {
  ContactDetailsFinanceCompanyBillingDto,
  ContactDetailsFinanceCompanyCommonDto,
  ContactDetailsFinanceCompanyDto,
} from '../model/contact-details-finance-company.dto';
import {
  ContactDeliveryMethod,
  ContactDetailsPaymentCreditorDto,
  ContactDetailsPaymentPayeeDto,
  ContactDetailsPaymentPayerDto,
  ContactDetailsTermsPayeeDto,
  ContactDetailsTermsPayerDto,
  ContactPayBy,
  ContactTermsPayables,
  ContactTermsReceivables,
} from '../model/contact-details-shared.dto';

const ContactTermsPayer = subentity<ContactDetailsTermsPayerDto>({
  paymentIsDue: enumeration(ContactTermsReceivables),
});

const ContactTermsPayee = subentity<ContactDetailsTermsPayeeDto>({
  paymentIsDue: enumeration(ContactTermsPayables),
});

const ContactCreditorDeliveryInfoMapper = subentity<ContactDetailsCreditorDeliveryInfoDto>({
  deliveryMethod: enumeration(ContactDeliveryMethod),
  emailMatch: enumeration(EmailMatch),
});

const ContactPaymentPayer = subentity<ContactDetailsPaymentPayerDto>({
  payBy: enumeration(ContactPayBy),
});

const ContactPaymentPayee = (paidBy: any) => subentity<ContactDetailsPaymentPayeeDto>({
  // @ts-expect-error Legacy typing suppression
  paidBy: enumFromDictionary(paidBy),
});

const ContactPaymentCreditor = (paidBy: any) => subentity<ContactDetailsPaymentCreditorDto>({
  // @ts-expect-error Legacy typing suppression
  paidBy: enumFromDictionary(paidBy),
});

const ContactDebtorBillingMapper = subentity<ContactDetailsDebtorBillingDto>({
  deliveryBy: enumeration(ContactDeliveryMethod),
});

const ContactFinanceCompanyBillingMapper = subentity<ContactDetailsFinanceCompanyBillingDto>({
  deliveryBy: enumeration(ContactDeliveryMethod),
});

export const ContactMergeMapper = (mappers: any) => {
  const { gender, paidBy, payBasis, payRoll, referredBy } = mappers;

  const ContactMergeCustomerMapper = subentity<ContactDetailsCustomerDto>({
    common: subentity<ContactDetailsCustomerCommonDto>({
      billing: subentity<ContactDetailsCustomerBillingDto>({
        deliveryBy: enumeration(ContactDeliveryMethod),
      }),
      referredBy: subentity<ContactDetailsCustomerReferredByDto>({
        referredBy: enumFromDictionary(referredBy),
      }),
    }),
    additionalDetails: subentity<ContactDetailsCustomerAdditionalDetailsDto>({}),
    payment: ContactPaymentPayer,
    terms: ContactTermsPayer,
  });

  const ContactMergeEmployeeMapper = subentity<ContactDetailsEmployeeDto>({
    common: subentity<ContactDetailsEmployeeCommonDto>({
      personal: subentity<ContactDetailsEmployeePersonalDto>({
        // @ts-expect-error Legacy typing suppression
        gender: enumFromDictionary(gender),
      }),
      salary: subentity<ContactDetailsEmployeeSalaryDto>({
        payrollCycle: enumFromDictionary(payRoll),
        payBasis: enumFromDictionary(payBasis),
      }),
    }),
    payment: ContactPaymentPayee(paidBy),
    terms: ContactTermsPayee,
  });

  const ContactMergeCreditorMapper = subentity<ContactDetailsCreditorDto>({
    common: subentity<ContactDetailsCreditorCommonDto>({
      type: enumeration(CreditorType),
      provides: enumeration(SupplierServiceType),
      category: enumeration(SupplierCategoryType),
      businessInfo: subentity<ContactDetailsCreditorBusinessInfoDto>({
        numberType: enumeration(BusinessNumberType),
        type: enumeration(WithholdingTaxType),
      }),
      orderBy: ContactCreditorDeliveryInfoMapper,
      billingBy: ContactCreditorDeliveryInfoMapper,
      scheduleBy: ContactCreditorDeliveryInfoMapper,
    }),
    payment: ContactPaymentCreditor(paidBy),
    terms: ContactTermsPayee,
  });

  const ContactMergeDebtorMapper = subentity<ContactDetailsDebtorDto>({
    common: subentity<ContactDetailsDebtorCommonDto>({
      billing: ContactDebtorBillingMapper,
    }),
    payment: ContactPaymentPayer,
    terms: ContactTermsPayer,
  });

  const ContactMergeFinanceCompanyMapper = subentity<ContactDetailsFinanceCompanyDto>({
    common: subentity<ContactDetailsFinanceCompanyCommonDto>({
      billing: ContactFinanceCompanyBillingMapper,
    }),
    payment: ContactPaymentPayer,
    terms: ContactTermsPayer,
  });

  return entity<ContactMergeDto>({
    type: enumeration(ContactRoleType),
    displayNameAs: enumeration(ContactDisplay),
    customer: ContactMergeCustomerMapper,
    employee: ContactMergeEmployeeMapper,
    creditorSupplier: ContactMergeCreditorMapper,
    debtor: ContactMergeDebtorMapper,
    financeCompany: ContactMergeFinanceCompanyMapper,
  });
};
